import {
    solarSystemData,
    ASTRONOMICAL_CONSTANTS,
    SCALE_CONFIG,
    calculateProportionalDistance,
} from './solarSystemData.js';

/**
 * Motor de simulación para el sistema solar heliocéntrico
 * Maneja cálculos de tiempo, coordenadas y posiciones planetarias
 */

export class SimulationEngine {
    constructor(systemData = solarSystemData) {
        this.solarSystemData = systemData;
        this.currentTime = 0; // tiempo en días desde época J2000
        this.timeScale = 1; // multiplicador de velocidad (días por segundo)
        this.isPaused = false;
        this.lastUpdateTime = 0;

        // Use imported scale configuration
        this.scaleConfig = SCALE_CONFIG;

        // Escala de distancia UNIFICADA (unidades de simulación por km), derivada del
        // sistema proporcional (la Tierra queda a 50 unidades). El movimiento galáctico
        // del Sol y las distancias orbitales DEBEN compartir esta escala; de lo contrario
        // la hélice es incoherente (antes el Sol usaba distanceScale=1e-9, ~334x más lento
        // que las órbitas proporcionales).
        this.heliocentricScale =
            this.scaleConfig.proportionalSizing.baseOrbitalDistance /
            this.scaleConfig.proportionalSizing.baseOrbitalRadius;

        // Use imported astronomical constants
        this.constants = {
            AU: ASTRONOMICAL_CONSTANTS.AU,
            SOLAR_VELOCITY: ASTRONOMICAL_CONSTANTS.SOLAR_GALACTIC_VELOCITY,
            SECONDS_PER_DAY: 86400, // Segundos en un día
        };
    }

    /**
     * Actualiza la simulación con el tiempo transcurrido
     * @param {number} deltaTime - Tiempo transcurrido en segundos
     */
    update(deltaTime) {
        if (this.isPaused) return;

        // Convertir deltaTime a días simulados
        const simulatedDays = deltaTime * this.timeScale * this.scaleConfig.timeScale;
        this.currentTime += simulatedDays;
        this.lastUpdateTime = performance.now();
    }

    /**
     * Establece la escala de tiempo de la simulación
     * @param {number} scale - Multiplicador de velocidad
     */
    setTimeScale(scale) {
        if (scale < 0) {
            throw new Error('Time scale must be non-negative');
        }
        this.timeScale = scale;
    }

    /**
     * Pausa la simulación
     */
    pause() {
        this.isPaused = true;
    }

    /**
     * Reanuda la simulación
     */
    resume() {
        this.isPaused = false;
    }

    /**
     * Reinicia la simulación al estado inicial
     */
    reset() {
        this.currentTime = 0;
        this.isPaused = false;
        this.timeScale = 1;
    }

    /**
     * Calcula la posición del sol en movimiento heliocéntrico
     * @returns {Object} Posición del sol {x, y, z} en unidades de simulación
     */
    getSolarPosition() {
        // El sol se mueve a través de la galaxia a ~220 km/s
        // Simplificamos asumiendo movimiento lineal en dirección Z
        const timeInSeconds = this.currentTime * this.constants.SECONDS_PER_DAY;
        const distanceTraveled = this.constants.SOLAR_VELOCITY * timeInSeconds;

        return {
            x: 0,
            y: 0,
            z: distanceTraveled * this.heliocentricScale,
        };
    }

    /**
     * Busca un planeta por id en los datos del sistema (que pueden inyectarse en el constructor).
     * @param {string} planetId - Identificador del planeta
     * @returns {Object|undefined} El planeta, o undefined si no existe
     */
    getPlanet(planetId) {
        return this.solarSystemData.planets.find((p) => p.id === planetId);
    }

    /**
     * Igual que getPlanet pero lanza un error descriptivo si el planeta no existe.
     * @param {string} planetId - Identificador del planeta
     * @returns {Object} El planeta
     */
    getPlanetOrThrow(planetId) {
        const planet = this.getPlanet(planetId);
        if (!planet) {
            throw new Error(`Planet ${planetId} not found`);
        }
        return planet;
    }

    /**
     * Calcula la posición de un planeta específico con trayectoria helicoidal
     * @param {string} planetId - Identificador del planeta
     * @returns {Object} Posición del planeta {x, y, z} en unidades de simulación
     */
    getPlanetPosition(planetId) {
        const planet = this.getPlanetOrThrow(planetId);

        // Calcular posición helicoidal (orbital + heliocéntrica)
        return this.calculateHelicalPosition(planet);
    }

    /**
     * Calcula la posición helicoidal de un planeta
     * Combina el movimiento orbital elíptico real con el movimiento heliocéntrico del sol
     * @param {Object} planet - Datos del planeta
     * @returns {Object} Posición helicoidal {x, y, z}
     */
    calculateHelicalPosition(planet) {
        // Calcular anomalía media (ángulo orbital basado en el período)
        const meanAnomaly = (this.currentTime / planet.orbitalPeriod) * 2 * Math.PI;

        // Usar el sistema de escalado proporcional para distancias orbitales
        const semiMajorAxis = this.calculateProportionalDistance(planet.orbitalRadius);

        // Obtener excentricidad orbital real
        const eccentricity = planet.orbitalEccentricity || 0;

        // Calcular posición orbital elíptica real
        const ellipticalPosition = this.calculateEllipticalOrbit(
            meanAnomaly,
            semiMajorAxis,
            eccentricity,
            planet.orbitalInclination || 0
        );

        // Obtener posición del sol (movimiento heliocéntrico)
        const solarPosition = this.getSolarPosition();

        // La trayectoria helicoidal combina:
        // - Movimiento orbital elíptico en 3D
        // - Movimiento heliocéntrico del sistema solar
        return {
            x: solarPosition.x + ellipticalPosition.x,
            y: solarPosition.y + ellipticalPosition.y,
            z: solarPosition.z + ellipticalPosition.z,
        };
    }

    /**
     * Calcula la posición en una órbita elíptica real usando la ecuación de Kepler
     * @param {number} meanAnomaly - Anomalía media en radianes
     * @param {number} semiMajorAxis - Semieje mayor de la elipse
     * @param {number} eccentricity - Excentricidad orbital (0 = círculo, <1 = elipse)
     * @param {number} inclination - Inclinación orbital en grados
     * @returns {Object} Posición orbital {x, y, z}
     */
    calculateEllipticalOrbit(meanAnomaly, semiMajorAxis, eccentricity, inclination = 0) {
        // Resolver la ecuación de Kepler para obtener la anomalía excéntrica
        const eccentricAnomaly = this.solveKeplerEquation(meanAnomaly, eccentricity);

        // Calcular la anomalía verdadera
        const trueAnomaly =
            2 *
            Math.atan2(
                Math.sqrt(1 + eccentricity) * Math.sin(eccentricAnomaly / 2),
                Math.sqrt(1 - eccentricity) * Math.cos(eccentricAnomaly / 2)
            );

        // Calcular la distancia radial (varía según la posición en la elipse)
        const radius = semiMajorAxis * (1 - eccentricity * Math.cos(eccentricAnomaly));

        // Posición en el plano orbital
        const x_orbital = radius * Math.cos(trueAnomaly);
        const y_orbital = radius * Math.sin(trueAnomaly);

        // Aplicar inclinación orbital (rotación alrededor del eje X)
        const inclinationRad = (inclination * Math.PI) / 180;
        const x = x_orbital;
        const y = y_orbital * Math.cos(inclinationRad);
        const z = y_orbital * Math.sin(inclinationRad);

        return { x, y, z };
    }

    /**
     * Resuelve la ecuación de Kepler usando el método de Newton-Raphson
     * M = E - e*sin(E), donde M = anomalía media, E = anomalía excéntrica, e = excentricidad
     * @param {number} meanAnomaly - Anomalía media en radianes
     * @param {number} eccentricity - Excentricidad orbital
     * @returns {number} Anomalía excéntrica en radianes
     */
    solveKeplerEquation(meanAnomaly, eccentricity) {
        // Para órbitas circulares (e ≈ 0), la anomalía excéntrica ≈ anomalía media
        if (eccentricity < 0.001) {
            return meanAnomaly;
        }

        // Estimación inicial
        let E = meanAnomaly;

        // Iteración de Newton-Raphson (máximo 10 iteraciones para convergencia)
        for (let i = 0; i < 10; i++) {
            const f = E - eccentricity * Math.sin(E) - meanAnomaly;
            const df = 1 - eccentricity * Math.cos(E);

            const deltaE = f / df;
            E -= deltaE;

            // Convergencia alcanzada
            if (Math.abs(deltaE) < 1e-8) {
                break;
            }
        }

        return E;
    }

    /**
     * Calcula la posición orbital de un planeta relativa al sol
     * @param {Object} planet - Datos del planeta
     * @returns {Object} Posición orbital {x, y, z}
     */
    calculateOrbitalPosition(planet) {
        // Calcular ángulo orbital basado en el período
        const orbitalAngle = (this.currentTime / planet.orbitalPeriod) * 2 * Math.PI;

        // Usar el sistema de escalado proporcional para distancias orbitales
        const scaledRadius = this.calculateProportionalDistance(planet.orbitalRadius);

        // Calcular posición en órbita circular (simplificada)
        return {
            x: Math.cos(orbitalAngle) * scaledRadius,
            y: 0, // Mantenemos órbitas en el plano para simplicidad inicial
            z: Math.sin(orbitalAngle) * scaledRadius,
        };
    }

    /**
     * Calcula la distancia orbital proporcional de un planeta
     * @param {number} realOrbitalRadius - Radio orbital real en km
     * @returns {number} Distancia orbital escalada para visualización
     */
    calculateProportionalDistance(realOrbitalRadius) {
        return calculateProportionalDistance(realOrbitalRadius);
    }

    /**
     * Convierte coordenadas astronómicas a coordenadas de simulación
     * @param {number} distance - Distancia en km
     * @returns {number} Distancia escalada para simulación
     */
    convertDistanceToSimulation(distance) {
        return distance * this.scaleConfig.distanceScale;
    }

    /**
     * Convierte tamaño astronómico a tamaño de simulación
     * @param {number} size - Tamaño en km
     * @returns {number} Tamaño escalado para simulación
     */
    convertSizeToSimulation(size) {
        return size * this.scaleConfig.sizeScale;
    }

    /**
     * Calcula la rotación actual de un planeta basada en su período de rotación
     * @param {string} planetId - Identificador del planeta
     * @returns {number} Ángulo de rotación en radianes
     */
    getPlanetRotation(planetId) {
        const planet = this.getPlanet(planetId);
        if (!planet || !planet.rotationPeriod) {
            return 0;
        }

        // Calcular rotación basada en el tiempo actual y período de rotación
        // Para rotación retrógrada (Venus y Urano), el período es negativo
        const rotationAngle = (this.currentTime / planet.rotationPeriod) * 2 * Math.PI;

        return rotationAngle;
    }

    /**
     * Obtiene todas las rotaciones planetarias actuales
     * @returns {Object} Mapa de rotaciones planetarias en radianes
     */
    getAllPlanetRotations() {
        const rotations = {};

        for (const planet of this.solarSystemData.planets) {
            rotations[planet.id] = this.getPlanetRotation(planet.id);
        }

        return rotations;
    }

    /**
     * Verifica si un planeta tiene rotación retrógrada
     * @param {string} planetId - Identificador del planeta
     * @returns {boolean} True si el planeta tiene rotación retrógrada
     */
    isPlanetRetrograde(planetId) {
        const planet = this.getPlanet(planetId);
        return planet && planet.rotationPeriod < 0;
    }

    /**
     * Obtiene el estado actual de la simulación
     * @returns {Object} Estado de la simulación
     */
    getSimulationState() {
        return {
            currentTime: this.currentTime,
            timeScale: this.timeScale,
            isPaused: this.isPaused,
            solarPosition: this.getSolarPosition(),
            planetRotations: this.getAllPlanetRotations(),
        };
    }

    /**
     * Obtiene todas las posiciones planetarias actuales
     * @returns {Object} Mapa de posiciones planetarias
     */
    getAllPlanetPositions() {
        const positions = {};

        for (const planet of this.solarSystemData.planets) {
            positions[planet.id] = this.getPlanetPosition(planet.id);
        }

        return positions;
    }

    /**
     * Calcula la velocidad heliocéntrica de un planeta en un momento dado
     * @param {string} planetId - Identificador del planeta
     * @param {number} deltaTime - Pequeño intervalo de tiempo para calcular velocidad (días)
     * @returns {Object} Velocidad {x, y, z} en unidades de simulación por día
     */
    getPlanetHelicalVelocity(planetId, deltaTime = 0.01) {
        const currentPos = this.getPlanetPosition(planetId);

        // Avanzar ligeramente el tiempo
        const originalTime = this.currentTime;
        this.currentTime += deltaTime;
        const futurePos = this.getPlanetPosition(planetId);

        // Restaurar tiempo original
        this.currentTime = originalTime;

        // Calcular velocidad
        return {
            x: (futurePos.x - currentPos.x) / deltaTime,
            y: (futurePos.y - currentPos.y) / deltaTime,
            z: (futurePos.z - currentPos.z) / deltaTime,
        };
    }

    /**
     * Verifica si un planeta está siguiendo una trayectoria helicoidal correcta
     * @param {string} planetId - Identificador del planeta
     * @param {number} timeSpan - Período de tiempo a verificar (días)
     * @returns {Object} Análisis de la trayectoria helicoidal
     */
    analyzeHelicalTrajectory(planetId, timeSpan = 365.25) {
        const planet = this.getPlanetOrThrow(planetId);

        const originalTime = this.currentTime;
        const positions = [];
        const steps = 100;
        const timeStep = timeSpan / steps;

        // Recopilar posiciones a lo largo del tiempo
        for (let i = 0; i <= steps; i++) {
            this.currentTime = originalTime + i * timeStep;
            positions.push({
                time: this.currentTime,
                position: this.getPlanetPosition(planetId),
            });
        }

        // Restaurar tiempo original
        this.currentTime = originalTime;

        // Analizar la trayectoria
        const analysis = {
            isHelical: true,
            orbitalRadius: this.calculateProportionalDistance(planet.orbitalRadius),
            solarDisplacement:
                this.constants.SOLAR_VELOCITY *
                (timeSpan * this.constants.SECONDS_PER_DAY) *
                this.heliocentricScale,
            positions: positions,
            averageOrbitalRadius: 0,
            zProgression: 0,
        };

        // Calcular radio orbital promedio
        let totalRadius = 0;
        for (const pos of positions) {
            const radius = Math.sqrt(
                pos.position.x * pos.position.x + pos.position.y * pos.position.y
            );
            totalRadius += radius;
        }
        analysis.averageOrbitalRadius = totalRadius / positions.length;

        // Verificar progresión en Z (movimiento heliocéntrico)
        const firstZ = positions[0].position.z;
        const lastZ = positions[positions.length - 1].position.z;
        analysis.zProgression = lastZ - firstZ;

        // Una trayectoria es helicoidal si su proyección XY permanece dentro de los
        // límites orbitales reales (perihelio..afelio) y avanza en Z (movimiento del Sol).
        // NO exigimos radio constante: las órbitas son elípticas (excentricidad real), por lo
        // que el radio varía entre a(1-e) y a(1+e). La inclinación comprime levemente el plano XY.
        const eccentricity = planet.orbitalEccentricity || 0;
        const perihelion = analysis.orbitalRadius * (1 - eccentricity);
        const aphelion = analysis.orbitalRadius * (1 + eccentricity);
        let withinOrbitalBounds = true;
        for (const pos of positions) {
            const radius = Math.sqrt(
                pos.position.x * pos.position.x + pos.position.y * pos.position.y
            );
            // Margen del 1% para tolerar la compresión por inclinación y el error numérico
            if (radius < perihelion * 0.99 || radius > aphelion * 1.01) {
                withinOrbitalBounds = false;
                break;
            }
        }
        analysis.isHelical = withinOrbitalBounds && Math.abs(analysis.zProgression) > 0;

        return analysis;
    }

    /**
     * Obtiene la distancia heliocéntrica total recorrida por el sistema solar
     * @returns {number} Distancia en unidades de simulación
     */
    getTotalHeliocentricDistance() {
        const timeInSeconds = this.currentTime * this.constants.SECONDS_PER_DAY;
        return this.constants.SOLAR_VELOCITY * timeInSeconds * this.heliocentricScale;
    }

    /**
     * Calcula el pitch (inclinación) de la hélice para un planeta dado
     * El pitch es la distancia que avanza el sol mientras el planeta completa una órbita
     * @param {string} planetId - Identificador del planeta
     * @returns {number} Pitch de la hélice en unidades de simulación
     */
    getHelicalPitch(planetId) {
        const planet = this.getPlanetOrThrow(planetId);

        // Distancia que recorre el sol durante un período orbital del planeta
        const orbitalPeriodSeconds = planet.orbitalPeriod * this.constants.SECONDS_PER_DAY;
        const solarDistance = this.constants.SOLAR_VELOCITY * orbitalPeriodSeconds;

        return solarDistance * this.heliocentricScale;
    }
}
